/**
 * 계정별 store 보관함 (IndexedDB)
 *
 * 계정을 바꿀 때 화면을 통째로 새로 그리는데(location.reload), 그러면 store 가 비워져
 * 상품DB 같은 큰 데이터를 매번 다시 받는다. 큰 회사는 상품DB만 16MB 가 넘는다.
 *
 * 그래서 떠나기 전에 그 계정의 store 를 여기에 넣어두고, 돌아오면 꺼내 쓴다.
 * 화면들은 이미 "store 에 없으면 서버에서 받는다"로 되어 있어서, 채워 넣기만 하면
 * 각 화면을 손대지 않아도 그대로 동작한다.
 *
 * ★ stamp 없이 쓰면 안 된다.
 *   스냅샷은 '내가 떠날 때의 값'이라, 그 사이 다른 사람이 수집하거나 값을 고쳤으면
 *   옛날 숫자를 보게 된다. 한 계정을 여럿이 보는 곳에서 이게 정확히 예전 사고였다.
 *   서버가 주는 '이 회사 데이터가 마지막으로 바뀐 시각'과 다르면 스냅샷을 버린다.
 */

const DB_NAME = 'couplus-store';
const DB_VERSION = 1;
const STORE = 'snapshots';

// 데이터가 바뀌면 stamp 가 반드시 갱신되므로(server.js 의 DATA_WRITE_PATHS 미들웨어)
// 유효시간은 길게 잡아도 낡은 값을 볼 일이 없다. 이건 stamp 를 못 받는 예외 상황의 안전망이다
const TTL_MS = 12 * 60 * 60 * 1000;   // 12시간

// 보관은 최근 몇 계정까지만. 계정이 늘수록 디스크가 계속 불어나기 때문이다
// (큰 회사는 상품DB 하나가 16MB 다 — 상한이 없으면 100 계정에 1.6GB).
// 실측(2026-09-04): 105건 보관해도 하나 꺼내기 5ms, 디스크는 허용량의 0.1% 였다.
// 그래서 개수는 넉넉히 두고, 진짜 안전장치는 위의 stamp 로 건다
const MAX_SNAPSHOTS = 20;

// 보관할 store 조각 — 계정 데이터만. 화면 상태(모달·탭·진행률 등)는 담지 않는다
export const SNAPSHOT_KEYS = [
    'growthdb',
    'growthSales',
    'rocketSales',
    'polist',
    'productdb',
    'stock',
    'purchase',
    'returns',
    'reqpurchase',
    'invoice',
    'exportlist',
    'matchpo',
    'sales',
    'supply',
    // 'settings' 는 담지 않는다 — loginstatus 가 늘 새로 주는 작은 문서라 보관 이득이 없고,
    // 저장(/api/savesetting)이 stamp 를 안 바꿔 옛 기본정보(브랜드·제조사)가 다시 얹혔다
    'categoryListData'
];

let dbPromise = null;

function openDB() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB 미지원'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'userId' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

function tx(mode) {
    return openDB().then(db => db.transaction(STORE, mode).objectStore(STORE));
}

/**
 * 지금 계정의 store 를 보관한다. 계정을 떠나기 직전에 부른다.
 * @param {string} userId
 * @param {object} state Vuex state
 * @param {string} stamp 서버가 준 데이터 변경 시각. 없으면(null) 보관하지 않는다
 */
export async function saveSnapshot(userId, state, stamp) {
    // 신선도를 확인할 방법이 없으면 아예 담지 않는다 — 낡은 값을 되살리는 것보다 다시 받는 게 낫다
    if (!userId || stamp === null || stamp === undefined) return;
    try {
        const data = {};
        let has = false;
        for (const k of SNAPSHOT_KEYS) {
            const v = state[k];
            if (v === undefined) continue;
            if (Array.isArray(v) && v.length === 0) continue;   // 빈 건 담을 이유가 없다
            data[k] = v;
            has = true;
        }
        if (!has) return;

        // Vue 반응성 프록시가 섞이면 IndexedDB 에 담기지 않으므로 순수 값으로 떼어낸다
        const plain = JSON.parse(JSON.stringify(data));

        await new Promise((resolve, reject) => {
            openDB().then(db => {
                const t = db.transaction(STORE, 'readwrite');
                t.objectStore(STORE).put({ userId, stamp: stamp || '', savedAt: Date.now(), data: plain });
                t.oncomplete = () => resolve();
                t.onerror = () => reject(t.error);
                t.onabort = () => reject(t.error || new Error('보관 중단'));
            }).catch(reject);
        });
        console.log('[storeSnapshot] 보관', Object.keys(data).join(', '));
        await prune();
    } catch (e) {
        // 자리가 없으면(QuotaExceededError) 전부 비우고 넘어간다.
        // 어차피 다시 받을 수 있는 값이라 버려도 잃는 게 없다
        if (e && (e.name === 'QuotaExceededError' || /quota/i.test(e.message || ''))) {
            console.warn('[storeSnapshot] 저장 공간이 부족해 보관함을 비웁니다');
            await clearAllSnapshots();
            return;
        }
        console.warn('[storeSnapshot] 보관 실패 (무시)', e.message);
    }
}

/** 오래됐거나 개수를 넘긴 보관분을 버린다. 디스크가 계속 불어나지 않게 하는 장치 */
async function prune() {
    try {
        const store = await tx('readwrite');
        const rows = await new Promise((resolve, reject) => {
            const r = store.getAll();
            r.onsuccess = () => resolve(r.result || []);
            r.onerror = () => reject(r.error);
        });
        const now = Date.now();
        const 버릴것 = rows
            .filter(r => now - r.savedAt > TTL_MS)                    // 이미 못 쓰는 것
            .concat(
                rows.filter(r => now - r.savedAt <= TTL_MS)
                    .sort((a, b) => b.savedAt - a.savedAt)            // 최근 것부터
                    .slice(MAX_SNAPSHOTS)                             // 개수 초과분
            );
        if (!버릴것.length) return;
        const del = await tx('readwrite');
        버릴것.forEach(r => del.delete(r.userId));
        console.log('[storeSnapshot] 오래된 보관분 정리', 버릴것.length, '건');
    } catch (e) {
        console.warn('[storeSnapshot] 정리 실패 (무시)', e.message);
    }
}

/**
 * 돌아왔을 때 꺼낸다. 없거나 낡았으면 null → 화면들이 평소대로 서버에서 받는다.
 * @param {string} userId
 * @param {string} stamp 지금 서버가 말하는 데이터 변경 시각
 */
export async function loadSnapshot(userId, stamp) {
    if (!userId || stamp === null || stamp === undefined) return null;
    try {
        const store = await tx('readonly');
        const row = await new Promise((resolve, reject) => {
            const r = store.get(userId);
            r.onsuccess = () => resolve(r.result || null);
            r.onerror = () => reject(r.error);
        });
        if (!row) return null;
        if (Date.now() - row.savedAt > TTL_MS) return null;
        // 떠난 사이에 누가 수집하거나 값을 고쳤으면 버린다
        if ((row.stamp || '') !== (stamp || '')) {
            console.log('[storeSnapshot] 그 사이 데이터가 바뀌어 버림');
            return null;
        }
        // 예전 코드가 담아 둔 칸(settings 등)은 버린다 — 지금 목록에 있는 칸만 돌려준다
        const data = {};
        for (const k of SNAPSHOT_KEYS) if (row.data && k in row.data) data[k] = row.data[k];
        return data;
    } catch (e) {
        console.warn('[storeSnapshot] 꺼내기 실패 — 서버에서 받습니다', e.message);
        return null;
    }
}

/** 로그아웃 때 전부 버린다 (공용 PC) */
export async function clearAllSnapshots() {
    try {
        const store = await tx('readwrite');
        store.clear();
        console.log('[storeSnapshot] 보관함 비움');
    } catch (e) {
        console.warn('[storeSnapshot] 비우기 실패 (무시)', e.message);
    }
}

export default { saveSnapshot, loadSnapshot, clearAllSnapshots, SNAPSHOT_KEYS };
